import React, { useState } from 'react';
import { TextField, Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { motion } from 'framer-motion';

const FileUpload = ({ onChange }) => {
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleExtractText = async (uploadedFile) => {
    const formData = new FormData();
    formData.append('contract', uploadedFile);

    try {
      setLoading(true);
      const res = await fetch('https://triunityiq.com/api.php/extract-text', {
        method: 'POST',
        body: formData,
      });

      const json = await res.json();
      setLoading(false);

      if (json.status === 1) {
        setText(json.text);
        onChange(json.text);
      } else {
        setError(json.msg || 'Failed to extract text');
      }
    } catch (err) {
      setLoading(false);
      setError('Server error while extracting text');
      console.error(err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type === 'application/pdf' ||
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        setFile(selectedFile);
        setError('');
        handleExtractText(selectedFile); // 🔥 Real API call
      } else {
        setError('Unsupported file type. Please upload a PDF or DOCX.');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (
        droppedFile.type === 'application/pdf' ||
        droppedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        setFile(droppedFile);
        setError('');
        handleExtractText(droppedFile); // 🔥 Real API call
      } else {
        setError('Unsupported file type. Please upload a PDF or DOCX.');
      }
    }
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
    onChange(e.target.value);
    setError('');
  };

  const clearFile = () => {
    setFile(null);
    setText('');
    onChange('');
    setError('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <Box
        sx={{
          border: '2px dashed',
          borderColor: 'grey.400',
          p: 3,
          borderRadius: 2,
          mb: 2,
          backgroundColor: 'background.paper',
          transition: 'border-color 0.3s ease',
          '&:hover': { borderColor: 'primary.main' },
        }}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <Typography sx={{ color: 'text.primary', mb: 2, display: 'flex', alignItems: 'center' }}>
          <CloudUpload sx={{ mr: 1 }} /> Drag & Drop PDF/DOCX or
        </Typography>

        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          sx={{ mb: 2, borderRadius: 2, color: 'primary.main', borderColor: 'grey.400' }}
        >
          Choose File
          <input type="file" accept=".pdf,.docx" hidden onChange={handleFileChange} />
        </Button>

        {file && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ color: 'text.secondary', mr: 1 }}>{file.name}</Typography>
            <IconButton onClick={clearFile} size="small">
              <Delete color="error" />
            </IconButton>
          </Box>
        )}

        {loading && (
          <Typography sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 1 }} /> Extracting text...
          </Typography>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Contract Text"
          value={text}
          onChange={handleTextChange}
          sx={{
            borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.400' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
            backgroundColor: 'background.paper',
          }}
        />
      </Box>
    </motion.div>
  );
};

export default FileUpload;
